import { useCallback, useEffect, useState } from "react";
import {
  entitlementsFor,
  formatBytes,
  formatPlanBytes,
  formatRelativeTime,
  PAYG_BYTES_PER_DOLLAR,
  PLANS,
  PLAN_IDS,
} from "@saucer/core";
import type { AdminAccount, AdminAccountUpdate, Plan, PlatformStats } from "@saucer/core";
import { loadAccounts, loadPlatformStats, updateAccount } from "../api.js";
import {
  Chart,
  DAY_LABELS,
  HOUR_LABELS,
  Meters,
  RANGES,
  Sparks,
  Tile,
  Toggle,
  flagFor,
} from "../components/charts.js";
import type { Metric } from "../components/charts.js";
import { DitherAvatar } from "../components/dither-avatar.js";

/**
 * Where an upgrade is actually applied.
 *
 * Nobody is billed automatically, so this page is the payment system: money
 * arrives however it arrives, and then somebody moves the account here. That
 * makes the note field load-bearing rather than decorative — an upgrade with
 * no record of why is one that quietly becomes permanent.
 */

const UNITS: Record<string, number> = {
  B: 1,
  KB: 1024,
  MB: 1024 ** 2,
  GB: 1024 ** 3,
  TB: 1024 ** 4,
};

/**
 * The override is entered as a size, not as a number of gigabytes.
 *
 * It used to be a plain GB field, which silently rounded any quota below half
 * a gigabyte to `0` — opening the editor on such an account and pressing Apply
 * would have set its quota to nothing. A bare number still means GB, because
 * that is what almost every real override is.
 */
function parseSize(text: string): number | null | undefined {
  const trimmed = text.trim();
  if (trimmed === "") return null;
  const match = /^([\d.]+)\s*(B|KB|MB|GB|TB)?$/i.exec(trimmed);
  if (!match) return undefined;
  const amount = Number(match[1]);
  if (!Number.isFinite(amount) || amount < 0) return undefined;
  return Math.round(amount * UNITS[(match[2] ?? "GB").toUpperCase()]!);
}

/** The inverse, in the largest unit that keeps the number exact. */
function showSize(bytes: number | null): string {
  if (bytes === null) return "";
  for (const unit of ["TB", "GB", "MB", "KB"]) {
    const scale = UNITS[unit]!;
    if (bytes >= scale && bytes % scale === 0) return `${bytes / scale} ${unit}`;
  }
  return `${bytes} B`;
}

type Tab = "overview" | "accounts";

export function Admin() {
  const [tab, setTab] = useState<Tab>("overview");

  return (
    <main className="stack narrow">
      <div className="section-head">
        <h1>Platform</h1>
        <div className="toggle">
          {(["overview", "accounts"] as Tab[]).map((id) => (
            <button
              key={id}
              type="button"
              className={`toggle__btn${tab === id ? " toggle__btn--on" : ""}`}
              onClick={() => setTab(id)}
            >
              {id}
            </button>
          ))}
        </div>
      </div>
      {tab === "overview" ? <Overview /> : <Accounts />}
    </main>
  );
}

/** Everything that has happened on saucer, from the cron's platform blob. */
function Overview() {
  const [stats, setStats] = useState<PlatformStats | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "pending" | "error">("loading");
  const [days, setDays] = useState<number>(30);
  const [metric, setMetric] = useState<Metric>("opens");

  useEffect(() => {
    loadPlatformStats()
      .then((body) => {
        setStats(body.stats);
        setState(body.stats ? "ready" : "pending");
      })
      .catch(() => setState("error"));
  }, []);

  if (state === "error") return <p className="notice notice--error">Could not load platform stats.</p>;
  if (state === "pending")
    return (
      <p className="notice">
        No rollup yet. The platform view is rebuilt every 15 minutes with the per-account ones.
      </p>
    );
  if (!stats) return <p className="meta">Loading…</p>;

  const { totals, storage, accounts } = stats;
  const window = stats.daily.slice(-days);
  const signups = accounts.signups.slice(-days);
  const newAccounts = signups.reduce((sum, day) => sum + day.uploads, 0);
  const fillShare = storage.grantedBytes > 0 ? storage.usedBytes / storage.grantedBytes : 0;

  return (
    <div className="stack">
      <span className="meta">Built {formatRelativeTime(stats.generatedAt)}</span>

      <section className="tiles">
        <Tile label="Accounts" value={accounts.total} />
        <Tile label="Stored" value={formatBytes(storage.usedBytes)} />
        <Tile label="Files" value={storage.files} />
        <Tile label="MRR" value={`$${storage.monthlyRevenue}`} hint="manual" />
        <Tile label="Views" value={totals.views} />
        <Tile label="Downloads" value={totals.downloads} />
        <Tile label="Uploads" value={totals.uploads} />
        <Tile label="Served" value={formatBytes(totals.bytesServed)} />
      </section>

      <p className="meta">
        {formatBytes(storage.usedBytes)} used of {formatBytes(storage.grantedBytes)} promised (
        {Math.round(fillShare * 100)}%) ·{" "}
        {accounts.byPlan.map((row) => `${row.accounts} ${PLANS[row.plan].label}`).join(" · ")} ·{" "}
        {accounts.withOverride} custom
        {accounts.overQuota > 0 ? (
          <>
            {" · "}
            <strong className="admin__alarm">{accounts.overQuota} over quota</strong>
          </>
        ) : null}
      </p>

      <section className="stack stack--tight">
        <div className="section-head">
          <h2>Activity</h2>
          <div className="toggles">
            <Toggle
              options={[
                { key: "opens", label: "opens" },
                { key: "bytes", label: "bytes" },
              ]}
              active={metric}
              onPick={(key) => setMetric(key as Metric)}
            />
            <Toggle
              options={RANGES.map((range) => ({ key: String(range.days), label: range.label }))}
              active={String(days)}
              onPick={(key) => setDays(Number(key))}
            />
          </div>
        </div>
        <Chart days={window} metric={metric} />
      </section>

      <section className="stack stack--tight">
        <h2>Signups</h2>
        <Chart
          days={signups}
          metric="opens"
          pick={(day) => day.uploads}
          describe={(day) => `${day.day} · ${day.uploads} new`}
        />
        <p className="meta">
          {newAccounts} new {newAccounts === 1 ? "account" : "accounts"} in the last {days} days.
        </p>
      </section>

      <section className="stack stack--tight">
        <h2>Biggest accounts</h2>
        <div className="scroller">
          <table className="grid">
            <thead>
              <tr>
                <th>Account</th>
                <th>Plan</th>
                <th className="grid__num">Files</th>
                <th className="grid__num">Stored</th>
                <th className="grid__num">Of quota</th>
                <th className="grid__num">Views</th>
              </tr>
            </thead>
            <tbody>
              {stats.accountRows.slice(0, 20).map((row) => {
                // `over` is the server's own test, not a threshold on `share`:
                // a quota of zero is a real setting — it suspends new uploads
                // while the existing files stay put — and no ratio can express
                // "holding bytes against no allowance at all".
                const over = row.bytes > row.quotaBytes;
                const share = row.quotaBytes > 0 ? row.bytes / row.quotaBytes : over ? 1 : 0;
                return (
                  <tr key={row.owner} className={over ? "admin__alarm" : undefined}>
                    <td className="mono grid__name">{row.owner}</td>
                    <td>{PLANS[row.plan].label}</td>
                    <td className="grid__num">{row.files.toLocaleString()}</td>
                    <td className="grid__num">{formatBytes(row.bytes)}</td>
                    <td className="grid__num">
                      {row.quotaBytes > 0 ? `${Math.round(share * 100)}%` : "no quota"}
                    </td>
                    <td className="grid__num">{row.views.toLocaleString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="stack stack--tight">
        <h2>Most opened, anywhere</h2>
        <div className="scroller">
          <table className="grid">
            <thead>
              <tr>
                <th>File</th>
                <th className="grid__num">Views</th>
                <th className="grid__num">Downloads</th>
                <th className="grid__num">Last opened</th>
              </tr>
            </thead>
            <tbody>
              {stats.topFiles.map((file) => (
                <tr key={`${file.owner}/${file.slug}`}>
                  <td className="mono grid__name">
                    {file.owner}/{file.slug}
                  </td>
                  <td className="grid__num">{file.views.toLocaleString()}</td>
                  <td className="grid__num">{file.downloads.toLocaleString()}</td>
                  <td className="grid__num meta">
                    {file.lastHit ? formatRelativeTime(file.lastHit) : "\u2014"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <div className="panes">
        <section className="stack stack--tight">
          <h2>Where from</h2>
          <Meters
            rows={stats.countries.map((row) => ({
              label: `${flagFor(row.country)} ${row.country || "unknown"}`,
              hits: row.hits,
            }))}
            empty="No opens with a country yet."
          />
        </section>
        <section className="stack stack--tight">
          <h2>Referrers</h2>
          <Meters rows={stats.referrers} empty="All direct." />
        </section>
        <section className="stack stack--tight">
          <h2>Platforms</h2>
          <Meters rows={stats.platforms} empty="No opens yet." />
        </section>
        <section className="stack stack--tight">
          <h2>By kind</h2>
          <Meters
            rows={stats.kinds.map((row) => ({ label: row.kind || "unknown", hits: row.events }))}
            empty="No events yet."
          />
        </section>
      </div>

      <section className="stack stack--tight">
        <h2>Rhythm</h2>
        <Sparks values={stats.hourly} labels={HOUR_LABELS} caption="Hour of day" />
        <Sparks values={stats.weekday} labels={DAY_LABELS} caption="Day of week" />
      </section>

      <section className="stack stack--tight">
        <h2>Latest, everywhere</h2>
        <ul className="feed">
          {stats.feed.map((event, index) => (
            <li key={`${event.at}-${index}`} className="feed__row">
              <span className={`chip chip--${event.type}`}>{event.type}</span>
              <span className="mono feed__slug">
                {event.owner}/{event.slug}
              </span>
              <span className="meta">{event.country || "\u2014"}</span>
              <span className="meta">{formatRelativeTime(event.at)}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function Accounts() {
  const [accounts, setAccounts] = useState<AdminAccount[] | null>(null);
  const [totals, setTotals] = useState({ accounts: 0, files: 0, bytes: 0 });
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");

  const refresh = useCallback(async () => {
    try {
      const page = await loadAccounts();
      setAccounts(page.accounts);
      setTotals(page.totals);
      setError(null);
    } catch {
      setError("Could not load accounts.");
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  if (error) return <p className="notice notice--error">{error}</p>;
  if (!accounts) return <p className="meta">Loading…</p>;

  const needle = query.trim().toLowerCase();
  const shown = needle
    ? accounts.filter((account) => account.username.toLowerCase().includes(needle))
    : accounts;

  return (
    <div className="stack">
      <span className="meta">
        {totals.accounts} accounts · {totals.files} files · {formatBytes(totals.bytes)} stored
      </span>

      <input
        className="input"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Filter by username"
        spellCheck={false}
      />

      <ul className="admin">
        {shown.map((account) => (
          <AccountRow key={account.id} account={account} onSaved={refresh} />
        ))}
      </ul>

      {shown.length === 0 ? <p className="meta">Nobody matches that.</p> : null}
    </div>
  );
}

function AccountRow({ account, onSaved }: { account: AdminAccount; onSaved: () => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const over = account.usage.bytes > account.effectiveQuotaBytes;
  const share =
    account.effectiveQuotaBytes > 0
      ? account.usage.bytes / account.effectiveQuotaBytes
      : over
        ? 1
        : 0;

  return (
    <li className={`admin__row${over ? " admin__row--over" : ""}`}>
      <button className="admin__summary" type="button" onClick={() => setOpen(!open)}>
        <span className="admin__who">
          {/* Seeded from the username, so the same account keeps the same glyph
              and a long list is scannable by shape rather than by reading. */}
          <DitherAvatar name={account.username} size={18} label="" animate={false} />
          <span className="mono">{account.username}</span>
          {account.isAdmin ? <span className="chip">admin</span> : null}
          <span className={`chip chip--plan-${account.plan}`}>{PLANS[account.plan].label}</span>
          {account.quotaBytes !== null ? <span className="chip">custom</span> : null}
        </span>

        <span className="admin__usage">
          <span className="quota__track" aria-hidden="true">
            <span
              className="quota__fill"
              style={{ inlineSize: `${Math.min(100, share * 100)}%` }}
            />
          </span>
          <span className="meta mono">
            {formatBytes(account.usage.bytes)} / {formatBytes(account.effectiveQuotaBytes)}
          </span>
        </span>
      </button>

      {account.planNote ? <p className="admin__note meta">{account.planNote}</p> : null}
      {open ? <Editor account={account} onSaved={onSaved} /> : null}
    </li>
  );
}

function Editor({ account, onSaved }: { account: AdminAccount; onSaved: () => Promise<void> }) {
  const [plan, setPlan] = useState<Plan>(account.plan);
  const [topups, setTopups] = useState(Math.round(account.bonusBytes / PAYG_BYTES_PER_DOLLAR));
  const [override, setOverride] = useState(showSize(account.quotaBytes));
  const [note, setNote] = useState(account.planNote ?? "");
  const [renews, setRenews] = useState(account.planRenewsAt ?? "");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const overrideBytes = parseSize(override);
  const valid = overrideBytes !== undefined;

  // What the account will actually get, shown before it is saved rather than
  // after — the interaction between plan, top-ups and an override is the one
  // thing that is easy to get wrong here.
  const preview = entitlementsFor({
    plan,
    bonusBytes: topups * PAYG_BYTES_PER_DOLLAR,
    quotaBytes: valid ? (overrideBytes as number | null) : null,
  });

  const save = async () => {
    if (!valid) return;
    setBusy(true);
    setProblem(null);
    const patch: AdminAccountUpdate = {
      plan,
      bonusBytes: topups * PAYG_BYTES_PER_DOLLAR,
      quotaBytes: overrideBytes as number | null,
      planNote: note.trim() === "" ? null : note.trim(),
      planRenewsAt: renews.trim() === "" ? null : renews.trim(),
    };
    try {
      await updateAccount(account.id, patch);
      await onSaved();
    } catch (error) {
      setProblem(error instanceof Error ? error.message : "That change did not stick.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="admin__editor stack stack--tight">
      <div className="seg">
        <span className="seg__label mono">Plan</span>
        {PLAN_IDS.map((id) => (
          <button
            key={id}
            type="button"
            className={`seg__option${plan === id ? " seg__option--on" : ""}`}
            onClick={() => setPlan(id)}
          >
            {PLANS[id].label} · {formatPlanBytes(PLANS[id].storageBytes)}
          </button>
        ))}
      </div>

      <label className="field">
        <span className="meta">
          Top-ups — {formatPlanBytes(PAYG_BYTES_PER_DOLLAR)} each, ${topups}/month
        </span>
        <input
          className="input"
          type="number"
          min={0}
          max={400}
          value={topups}
          onChange={(event) => setTopups(Math.max(0, Number(event.target.value) || 0))}
        />
      </label>

      <label className="field">
        <span className="meta">
          Override the whole quota — blank means plan + top-ups. A bare number is GB; "500 MB"
          and "2 TB" also work.
        </span>
        <input
          className="input mono"
          value={override}
          onChange={(event) => setOverride(event.target.value)}
          placeholder="—"
          spellCheck={false}
        />
      </label>

      <label className="field">
        <span className="meta">Why — who paid, how, and for what</span>
        <input
          className="input"
          value={note}
          onChange={(event) => setNote(event.target.value)}
          placeholder="Paid $10 by UPI on 22 Aug"
          maxLength={500}
        />
      </label>

      <label className="field">
        <span className="meta">Renews on — so a lapsed subscription is visible</span>
        <input
          className="input mono"
          value={renews}
          onChange={(event) => setRenews(event.target.value)}
          placeholder="2026-09-22"
          maxLength={40}
        />
      </label>

      <p className="notice">
        Result: <strong>{formatBytes(preview.storageBytes)}</strong> of storage, files up to{" "}
        {formatBytes(preview.maxFileBytes)}
        {preview.overridden ? " (quota pinned by hand)" : ""}. This account is using{" "}
        {formatBytes(account.usage.bytes)}.
      </p>

      {problem ? <p className="notice notice--error">{problem}</p> : null}

      <div className="row">
        <button className="button button--primary" type="button" onClick={save} disabled={busy || !valid}>
          {busy ? "Saving…" : "Apply"}
        </button>
        {!valid ? <span className="meta">That is not a size — try 40, 500 MB, 2 TB.</span> : null}
      </div>
    </div>
  );
}
